export type Role = "super_admin" | "admin" | "sales_manager" | "sales_executive" | "viewer";

export type Status = "Active" | "Inactive";

export type Branch = "Mumbai HQ" | "Delhi Branch" | "Bangalore Office" | "Hyderabad Branch" | "Pune Office";

export type PermissionLevel = "full" | "view" | "none";

// ── Full 14-module list ──
export type Module =
  | "Dashboard"
  | "Leads"
  | "Pipeline"
  | "Quotations"
  | "Calls"
  | "WhatsApp"
  | "Integrations"
  | "Reports"
  | "Settings — Users"
  | "Settings — Roles"
  | "Settings — Assignment"
  | "Settings — Scoring"
  | "Settings — Industry"
  | "Settings — Company";

// ── Sub-permission structures ──
export interface SubPermission {
  key: string;
  label: string;
}

export const leadsSubPermissions: SubPermission[] = [
  { key: "view_own", label: "View own leads only" },
  { key: "view_all", label: "View all leads" },
  { key: "create", label: "Create leads" },
  { key: "edit", label: "Edit leads" },
  { key: "delete", label: "Delete leads" },
  { key: "export", label: "Export leads" },
  { key: "bulk_assign", label: "Bulk assign" },
];

export const reportsSubPermissions: SubPermission[] = [
  { key: "view_own", label: "View own performance only" },
  { key: "view_team", label: "View team performance" },
  { key: "view_all", label: "View all agents" },
  { key: "export", label: "Export reports" },
];

export const settingsSubPermissions: SubPermission[] = [
  { key: "view", label: "View only" },
  { key: "edit", label: "Edit configurations" },
  { key: "add_delete", label: "Add/delete users" },
];

// Which modules have sub-permissions
export const moduleSubPermissionsMap: Partial<Record<Module, SubPermission[]>> = {
  "Leads": leadsSubPermissions,
  "Reports": reportsSubPermissions,
  "Settings — Users": settingsSubPermissions,
  "Settings — Roles": settingsSubPermissions,
  "Settings — Assignment": settingsSubPermissions,
  "Settings — Scoring": settingsSubPermissions,
  "Settings — Industry": settingsSubPermissions,
  "Settings — Company": settingsSubPermissions,
};

// ── Flat module list (14 items) ──
export const modules: Module[] = [
  "Dashboard",
  "Leads",
  "Pipeline",
  "Quotations",
  "Calls",
  "WhatsApp",
  "Integrations",
  "Reports",
  "Settings — Users",
  "Settings — Roles",
  "Settings — Assignment",
  "Settings — Scoring",
  "Settings — Industry",
  "Settings — Company",
];

// ── Role definition ──
export type RoleType = "system" | "custom";

export interface RoleDefinition {
  id: string;
  name: string;
  type: RoleType;
  systemKey?: Role;
  description: string;
  color: string;
  userCount: number;
  permissions: Record<Module, PermissionLevel>;
  subPermissions: Record<string, string[]>;
  createdAt?: string;
}

// ── Default system role permissions (14 modules) ──
const superAdminPerms: Record<Module, PermissionLevel> = {
  "Dashboard": "full",
  "Leads": "full",
  "Pipeline": "full",
  "Quotations": "full",
  "Calls": "full",
  "WhatsApp": "full",
  "Integrations": "full",
  "Reports": "full",
  "Settings — Users": "full",
  "Settings — Roles": "full",
  "Settings — Assignment": "full",
  "Settings — Scoring": "full",
  "Settings — Industry": "full",
  "Settings — Company": "full",
};

const adminPerms: Record<Module, PermissionLevel> = {
  ...superAdminPerms,
  "Settings — Roles": "view",
};

const salesManagerPerms: Record<Module, PermissionLevel> = {
  "Dashboard": "full",
  "Leads": "full",
  "Pipeline": "full",
  "Quotations": "full",
  "Calls": "full",
  "WhatsApp": "full",
  "Integrations": "none",
  "Reports": "view",
  "Settings — Users": "none",
  "Settings — Roles": "none",
  "Settings — Assignment": "none",
  "Settings — Scoring": "none",
  "Settings — Industry": "none",
  "Settings — Company": "none",
};

const salesExecutivePerms: Record<Module, PermissionLevel> = {
  "Dashboard": "view",
  "Leads": "full",
  "Pipeline": "view",
  "Quotations": "full",
  "Calls": "full",
  "WhatsApp": "full",
  "Integrations": "none",
  "Reports": "view",
  "Settings — Users": "none",
  "Settings — Roles": "none",
  "Settings — Assignment": "none",
  "Settings — Scoring": "none",
  "Settings — Industry": "none",
  "Settings — Company": "none",
};

const viewerPerms: Record<Module, PermissionLevel> = {
  "Dashboard": "view",
  "Leads": "view",
  "Pipeline": "view",
  "Quotations": "none",
  "Calls": "none",
  "WhatsApp": "none",
  "Integrations": "none",
  "Reports": "none",
  "Settings — Users": "none",
  "Settings — Roles": "none",
  "Settings — Assignment": "none",
  "Settings — Scoring": "none",
  "Settings — Industry": "none",
  "Settings — Company": "none",
};

// ── Default sub-permissions for system roles ──
const superAdminSubs = {
  "Leads": ["view_own", "view_all", "create", "edit", "delete", "export", "bulk_assign"],
  "Reports": ["view_own", "view_team", "view_all", "export"],
  "Settings — Users": ["view", "edit", "add_delete"],
  "Settings — Roles": ["view", "edit", "add_delete"],
  "Settings — Assignment": ["view", "edit", "add_delete"],
  "Settings — Scoring": ["view", "edit", "add_delete"],
  "Settings — Industry": ["view", "edit", "add_delete"],
  "Settings — Company": ["view", "edit", "add_delete"],
};

const adminSubs = { ...superAdminSubs, "Settings — Roles": ["view"] };

const salesManagerSubs = {
  "Leads": ["view_all", "create", "edit", "export", "bulk_assign"],
  "Reports": ["view_team"],
};

const salesExecutiveSubs = {
  "Leads": ["view_own", "create", "edit"],
  "Reports": ["view_own"],
};

const viewerSubs = {
  "Leads": ["view_own"],
};

// ── System role definitions ──
export const systemRoles: RoleDefinition[] = [
  {
    id: "system-super_admin",
    name: "Super Admin",
    type: "system",
    systemKey: "super_admin",
    description: "Full access to everything",
    color: "#8b5cf6",
    userCount: 1,
    permissions: superAdminPerms,
    subPermissions: superAdminSubs,
  },
  {
    id: "system-admin",
    name: "Admin",
    type: "system",
    systemKey: "admin",
    description: "Manage company settings and users",
    color: "#3b82f6",
    userCount: 0,
    permissions: adminPerms,
    subPermissions: adminSubs,
  },
  {
    id: "system-sales_manager",
    name: "Sales Manager",
    type: "system",
    systemKey: "sales_manager",
    description: "View team leads, reassign, access reports",
    color: "#f59e0b",
    userCount: 1,
    permissions: salesManagerPerms,
    subPermissions: salesManagerSubs,
  },
  {
    id: "system-sales_executive",
    name: "Sales Executive",
    type: "system",
    systemKey: "sales_executive",
    description: "Manage own leads and calls",
    color: "#22c55e",
    userCount: 2,
    permissions: salesExecutivePerms,
    subPermissions: salesExecutiveSubs,
  },
  {
    id: "system-viewer",
    name: "Viewer",
    type: "system",
    systemKey: "viewer",
    description: "Read-only access",
    color: "#64748b",
    userCount: 0,
    permissions: viewerPerms,
    subPermissions: viewerSubs,
  },
];

// ── Mock custom role ──
const teamLeadPerms: Record<Module, PermissionLevel> = {
  "Dashboard": "full",
  "Leads": "full",
  "Pipeline": "full",
  "Quotations": "full",
  "Calls": "full",
  "WhatsApp": "full",
  "Integrations": "none",
  "Reports": "view",
  "Settings — Users": "view",
  "Settings — Roles": "none",
  "Settings — Assignment": "none",
  "Settings — Scoring": "none",
  "Settings — Industry": "none",
  "Settings — Company": "none",
};

const teamLeadSubs = {
  "Leads": ["view_all", "create", "edit"],
  "Reports": ["view_team"],
  "Settings — Users": ["view"],
};

export const initialCustomRoles: RoleDefinition[] = [
  {
    id: "custom-1",
    name: "Team Lead",
    type: "custom",
    description: "Senior sales agent with team visibility and limited settings access",
    color: "#f59e0b",
    userCount: 1,
    permissions: teamLeadPerms,
    subPermissions: teamLeadSubs,
    createdAt: "2026-05-10",
  },
];

// ── All roles combined ──
export function getAllRoles(customRoles: RoleDefinition[]): RoleDefinition[] {
  return [...systemRoles, ...customRoles];
}

// ── Role color swatches ──
export const roleColorOptions = [
  { value: "#f59e0b", name: "Amber" },
  { value: "#22c55e", name: "Green" },
  { value: "#3b82f6", name: "Blue" },
  { value: "#4f46e5", name: "Indigo" },
  { value: "#8b5cf6", name: "Purple" },
  { value: "#ec4899", name: "Pink" },
  { value: "#ef4444", name: "Red" },
  { value: "#14b8a6", name: "Teal" },
];

// ── Template role for "copy from" dropdown ──
export const templateRoleOptions = [
  { value: "blank", label: "Blank (no permissions)" },
  { value: "super_admin", label: "Super Admin" },
  { value: "admin", label: "Admin" },
  { value: "sales_manager", label: "Sales Manager" },
  { value: "sales_executive", label: "Sales Executive" },
  { value: "viewer", label: "Viewer" },
];

// ── Permission labels / colors (keep existing for backward compat) ──
export const permissionLabels: Record<PermissionLevel, string> = {
  full: "Full Access",
  view: "View Only",
  none: "No Access",
};

export const permissionCellColors: Record<PermissionLevel, string> = {
  full: "bg-green-100 text-green-700 border-green-200",
  view: "bg-amber-100 text-amber-800 border-amber-200",
  none: "bg-red-100 text-red-700 border-red-200",
};

// ── Permission toggle button colors (active state) ──
export const permissionToggleActive: Record<PermissionLevel, string> = {
  full: "bg-green-50 text-green-700 border-green-300",
  view: "bg-amber-50 text-amber-700 border-amber-300",
  none: "bg-red-50 text-red-600 border-red-300",
};

// ── Existing user types (unchanged) ──
export interface CrmUser {
  id: string;
  name: string;
  email: string;
  password: string;
  phone: string;
  role: Role;
  branch: Branch;
  status: Status;
  lastLogin: string;
}

export interface LoginUser {
  id: string;
  name: string;
  email: string;
  password: string;
  role: Role;
}

export const mockLoginUsers: LoginUser[] = [
  { id: "1", name: "Rajesh Kumar", email: "admin@crm.com", password: "admin123", role: "super_admin" },
  { id: "2", name: "Priya Sharma", email: "manager@crm.com", password: "manager123", role: "sales_manager" },
  { id: "3", name: "Amit Patel", email: "exec@crm.com", password: "exec123", role: "sales_executive" },
];

export const allUsers: CrmUser[] = [
  {
    id: "1", name: "Rajesh Kumar", email: "admin@crm.com", password: "admin123",
    phone: "+91 98201 23456", role: "super_admin", branch: "Mumbai HQ",
    status: "Active", lastLogin: "2026-06-25 10:15 AM",
  },
  {
    id: "2", name: "Priya Sharma", email: "manager@crm.com", password: "manager123",
    phone: "+91 98112 34567", role: "sales_manager", branch: "Mumbai HQ",
    status: "Active", lastLogin: "2026-06-25 09:30 AM",
  },
  {
    id: "3", name: "Amit Patel", email: "exec@crm.com", password: "exec123",
    phone: "+91 98765 43210", role: "sales_executive", branch: "Delhi Branch",
    status: "Active", lastLogin: "2026-06-24 04:45 PM",
  },
  {
    id: "4", name: "Sneha Reddy", email: "sneha.reddy@crm.com", password: "sneha123",
    phone: "+91 98480 12340", role: "admin", branch: "Mumbai HQ",
    status: "Active", lastLogin: "2026-06-25 11:00 AM",
  },
  {
    id: "5", name: "Vikram Desai", email: "vikram.desai@crm.com", password: "vikram123",
    phone: "+91 98330 56789", role: "sales_manager", branch: "Bangalore Office",
    status: "Active", lastLogin: "2026-06-24 03:20 PM",
  },
  {
    id: "6", name: "Anjali Nair", email: "anjali.nair@crm.com", password: "anjali123",
    phone: "+91 98450 98765", role: "sales_executive", branch: "Bangalore Office",
    status: "Active", lastLogin: "2026-06-25 08:15 AM",
  },
  {
    id: "7", name: "Rahul Joshi", email: "rahul.joshi@crm.com", password: "rahul123",
    phone: "+91 98230 45678", role: "sales_executive", branch: "Delhi Branch",
    status: "Inactive", lastLogin: "2026-06-10 02:30 PM",
  },
  {
    id: "8", name: "Kavita Iyer", email: "kavita.iyer@crm.com", password: "kavita123",
    phone: "+91 98440 11223", role: "viewer", branch: "Hyderabad Branch",
    status: "Active", lastLogin: "2026-06-24 11:45 AM",
  },
  {
    id: "9", name: "Deepak Malhotra", email: "deepak.m@crm.com", password: "deepak123",
    phone: "+91 98100 33445", role: "sales_manager", branch: "Pune Office",
    status: "Active", lastLogin: "2026-06-25 07:50 AM",
  },
  {
    id: "10", name: "Meera Chopra", email: "meera.c@crm.com", password: "meera123",
    phone: "+91 98990 66778", role: "viewer", branch: "Mumbai HQ",
    status: "Active", lastLogin: "2026-06-23 05:10 PM",
  },
];

export const roleLabels: Record<Role, string> = {
  super_admin: "Super Admin",
  admin: "Admin",
  sales_manager: "Sales Manager",
  sales_executive: "Sales Executive",
  viewer: "Viewer",
};

export const roleBadgeColors: Record<Role, string> = {
  super_admin: "bg-violet-100 text-violet-700",
  admin: "bg-blue-100 text-blue-700",
  sales_manager: "bg-amber-100 text-amber-800",
  sales_executive: "bg-green-100 text-green-700",
  viewer: "bg-gray-100 text-gray-700",
};

export const statusColors: Record<Status, string> = {
  Active: "bg-green-100 text-green-700",
  Inactive: "bg-red-100 text-red-700",
};

export const stageBadgeColors: Record<string, string> = {
  Negotiation: "bg-amber-100 text-amber-800",
  Proposal: "bg-blue-100 text-blue-700",
  Qualified: "bg-violet-100 text-violet-700",
  "Closed Won": "bg-green-100 text-green-700",
  Discovery: "bg-gray-100 text-gray-700",
};

export const branches: Branch[] = [
  "Mumbai HQ",
  "Delhi Branch",
  "Bangalore Office",
  "Hyderabad Branch",
  "Pune Office",
];

export const roleOptions: { value: Role; label: string }[] = [
  { value: "super_admin", label: "Super Admin" },
  { value: "admin", label: "Admin" },
  { value: "sales_manager", label: "Sales Manager" },
  { value: "sales_executive", label: "Sales Executive" },
  { value: "viewer", label: "Viewer" },
];

// ── Legacy: kept for backward compat (old 6-module structure) ──
export interface RolePermissions {
  role: Role;
  permissions: Record<string, PermissionLevel>;
}

export const defaultRolePermissions: RolePermissions[] = [
  {
    role: "super_admin",
    permissions: { Leads: "full", Pipeline: "full", Calls: "full", Reports: "full", Integrations: "full", Settings: "full" },
  },
  {
    role: "admin",
    permissions: { Leads: "full", Pipeline: "full", Calls: "full", Reports: "full", Integrations: "view", Settings: "full" },
  },
  {
    role: "sales_manager",
    permissions: { Leads: "full", Pipeline: "full", Calls: "full", Reports: "view", Integrations: "none", Settings: "none" },
  },
  {
    role: "sales_executive",
    permissions: { Leads: "view", Pipeline: "view", Calls: "full", Reports: "none", Integrations: "none", Settings: "none" },
  },
  {
    role: "viewer",
    permissions: { Leads: "view", Pipeline: "view", Calls: "view", Reports: "none", Integrations: "none", Settings: "none" },
  },
];